import * as React from 'react';
import {useMemo} from 'react';
import type {SxProps} from '@mui/material';
import {Select} from 'components/fields/Select';
import {useLocalization} from '../../localization';
import {FlexBox} from '../layout';

const wrapperSx: SxProps = {
  textWrap: 'nowrap',
};

type Props = {
  size: number;
  setSize: (size: number) => void;
  pageSizes: ReadonlyArray<
    | number
    | {
        value: number;
        label: string;
      }
  >;
};

/**
 * PageSizePicker component allows users to select the number of items per page.
 * Value -1 means all items should be displayed.
 */
export const PageSizePicker: React.FC<Props> = ({size, setSize, pageSizes}) => {
  const {t} = useLocalization();
  const pageSizeOptions: ReadonlyArray<
    | number
    | {
        value: number;
        label: string;
      }
  > = useMemo(() => {
    const options = pageSizes.map(o =>
      isAllOption(o)
        ? {value: 2000, label: t('search.result.all')} // Hard limit to 2000 items
        : {value: typeof o === 'object' ? o.value : o, label: o.toString()},
    );

    // Always keep current option.
    if (!options.find(o => o.value === size)) {
      options.push({value: size, label: size.toString()});
    }

    return options;
  }, [pageSizes, size, t]);

  return (
    <FlexBox
      alignItems={'center'}
      horizontal
      alignContent={'center'}
      justifyContent={'center'}
      sx={wrapperSx}>
      <Select<number>
        value={size}
        options={pageSizeOptions}
        onChange={value => {
          const size = Number(value);
          setSize(size);
        }}
      />
    </FlexBox>
  );
};

const isAllOption = (
  option: number | {value: number; label: string},
): boolean => {
  return (
    option === -1 ||
    (typeof option === 'object' && 'value' in option && option.value === -1)
  );
};
